import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react"
import useDuplicatedCallback from "../helpers/useDeduplicatedCallback";
import useFeature from "../components/Shared/FeatureProvider/useFeature";

// Create Scanning Context
const ScanningContext = createContext({
    scanningCapabilities: 'none',
    displayScanButton: false,
    scanInProgress: false,
    startScan: () => { },
    stopScan: () => { },
    triggerScan: () => { },
    registerCallback: () => { },
    unregisterCallback: () => { },
});

const scanningOptions = { camera:{enabled:true}};
const STORAGE_KEY = 'scanning_options';
const subscribe =  (callback) => {
    window.addEventListener('storage', callback, false)

    return () => {
        window.removeEventListener('storage', callback);
    }
}
const getScanningOptionsStr = () => {
    const optionsString = window.localStorage.getItem(STORAGE_KEY)
    return optionsString ?? JSON.stringify(scanningOptions)

}

const setScanningOptions = (options) => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(options))
    window.dispatchEvent(new Event("storage"));
}

const toggleCameraScanning = (enabled) => {
    const optionsStr = getScanningOptionsStr()
    const options = JSON.parse(optionsStr)
    const newOptions = {
        ...options,
        camera: {
            enabled,
        }
    }
    setScanningOptions(newOptions)
}


// Provider Component
export const ScanningProvider = ({ children }) => {
    const [activeCallbacks, setActiveCallbacks] = useState(new Set())
    const [scanInProgress, setScanInProgress] = useState(false);

    const {isEnabled} = useFeature()


    const scanningCapabilities = useMemo(() => {
        const capabilities = window?.Android?.getScanningCapabilities?.()
        if(capabilities && (capabilities === 'camera' && !isEnabled('camera_scanning'))){
            return 'none';
        }
        return capabilities || 'none';
    }, [isEnabled]);

    const optionsStr = useSyncExternalStore(subscribe, getScanningOptionsStr);
     const options = useMemo(() => {
        try {
        return JSON.parse(optionsStr);
        } catch (e) {
        console.error("Failed to parse scanning options from localStorage", e);
        return scanningOptions;
        }
    }, [optionsStr]);

    const hasActiveCallbacks = useMemo(() => activeCallbacks.size > 0, [activeCallbacks])
    //TMP Currently disabling display of scan button as camera scanning is not stable enough
    const displayScanButton = useMemo(() => scanningCapabilities === 'camera' && hasActiveCallbacks && options.camera.enabled, [scanningCapabilities, hasActiveCallbacks,options]);


    const barcodeCameraEventHandler = useDuplicatedCallback((event) => {
        if (event.value === 'start') {
            setScanInProgress(true);
        }
        else if (event.value === 'stop') {
            setScanInProgress(false);
        }
    },[], 300);

    useEffect(() => {
        window.addEventListener('barcode.camera', barcodeCameraEventHandler, false)

        return () => {
            window.removeEventListener('barcode.camera', barcodeCameraEventHandler);
        }
    }
        , [barcodeCameraEventHandler])

    const startScan = useCallback(() => {
        if (scanningCapabilities === 'camera' && window?.Android?.enableCameraScanning) {
            window.Android.enableCameraScanning();
            setScanInProgress(true);
        }
    }, [scanningCapabilities]);

    const stopScan = useCallback(() => {
        if (scanningCapabilities === 'camera' && window?.Android?.disableCameraScanning) {
            window.Android.disableCameraScanning();
            setScanInProgress(false);
        }
    }, [])

    const registerCallback = useCallback((id) => {
        setActiveCallbacks(prev => new Set(prev).add(id))
    }, [])

    const unregisterCallback = useCallback((id) => {
        setActiveCallbacks(prev => {
            const next = new Set(prev)
            next.delete(id)

            // If no more callbacks and scan is in progress, stop it
            if (next.size === 0 && scanInProgress) {
                if (scanningCapabilities === 'camera' && window?.Android?.disableCameraScanning) {
                    window.Android.disableCameraScanning();
                    setScanInProgress(false);
                }
            }

            return next
        })
    }, [scanInProgress, scanningCapabilities])

    const contextValue = useMemo(() => ({
        scanningCapabilities,
        displayScanButton,
        scanInProgress,
        startScan,
        stopScan,
        registerCallback,
        unregisterCallback,
        toggleCameraScanning,
    }), [scanningCapabilities, displayScanButton, scanInProgress, startScan, stopScan, registerCallback, unregisterCallback])

    return (
        <ScanningContext.Provider value={contextValue}>
            {children}
        </ScanningContext.Provider>
    )
}

export const useScanningContext = () => useContext(ScanningContext)
